import React from 'react';
import { Search, X } from 'lucide-react';
import { VoucherListStatus, VoucherType } from '@/client/types/voucher';

export interface VoucherFilters {
  status: string;
  vtid: string;
}

interface VoucherListFiltersProps {
  searchValue: string;
  filters: VoucherFilters;
  types: VoucherType[];
  onSearch: (value: string) => void;
  onFilterChange: (key: keyof VoucherFilters, value: string) => void;
  onReset?: () => void;
}

const STATUS_OPTIONS: { label: string; value: VoucherListStatus | '' }[] = [
  { label: 'All statuses', value: '' },
  { label: 'Active', value: 'active' },
  { label: 'Revoked', value: 'revoked' },
  { label: 'Redeemed', value: 'redeemed' },
  { label: 'Expired', value: 'expired' },
];

const VoucherListFilters: React.FC<VoucherListFiltersProps> = ({
  searchValue,
  filters,
  types,
  onSearch,
  onFilterChange,
  onReset,
}) => {
  const hasActiveFilters = filters.status !== '' || filters.vtid !== '' || searchValue !== '';

  return (
    <div className="mb-6 rounded-lg bg-white p-4 shadow">
      <div className="flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search by code…"
            value={searchValue}
            onChange={(e) => onSearch(e.target.value)}
            className="w-full rounded-md border border-gray-300 py-2 pl-9 pr-4 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <select
          value={filters.status}
          onChange={(e) => onFilterChange('status', e.target.value)}
          className="min-w-[150px] rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          {STATUS_OPTIONS.map((opt) => (
            <option key={opt.value || 'all'} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>

        <select
          value={filters.vtid}
          onChange={(e) => onFilterChange('vtid', e.target.value)}
          className="min-w-[180px] rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">All types</option>
          {types.map((t) => (
            <option key={t.vtid} value={t.vtid}>
              {t.name}
            </option>
          ))}
        </select>

        {hasActiveFilters && onReset && (
          <button
            type="button"
            onClick={onReset}
            className="flex items-center gap-1.5 whitespace-nowrap rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-600 transition-colors hover:bg-gray-50"
          >
            <X className="h-4 w-4" />
            Clear
          </button>
        )}
      </div>
    </div>
  );
};

export default VoucherListFilters;
